import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { useConfirmation } from '../ui/ConfirmationDialog';
import { 
  Upload, 
  X, 
  Save, 
  CheckCircle,
  FileText,
  Wand2
} from 'lucide-react';
import { useCreateRequirement, useUpdateRequirement } from '../../lib/hooks/useApi';
import { useJDProcessing } from '../../hooks/useJDProcessing';
import { JDProcessingProgress } from '../ui/JDProcessingProgress';
import { ProcessingStage } from '../../lib/types/api';
import { ClientSelector } from '../clients/ClientSelector';

// Stage labels for progress display
const stageLabels: Record<ProcessingStage, string> = {
  uploading: 'Uploading File',
  extracting: 'Extracting Text',
  parsing: 'Parsing with AI',
  validating: 'Validating Data',
  completed: 'Completed'
};
import { Button } from '../ui/Button';
import { DraggableSkillsInput } from '../ui/DraggableSkillsInput';
import { apiClient } from '../../lib/api/client';
import { LoadingSpinner } from '../ui/LoadingSpinner';

// Lightweight SPOC selector tied to selected client
const SPOCSelector: React.FC<{ clientId?: string; value?: string; onChange: (spoc?: { id: string; name: string; email?: string; phone?: string }) => void }>=({ clientId, value, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const [spocs, setSpocs] = React.useState<Array<{ id: string; name: string; email?: string; phone?: string }>>([]);

  React.useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        if (!clientId) { setSpocs([]); return; }
        const list = await apiClient.getClientSPOCs(clientId);
        if (active) setSpocs(list || []);
      } catch {
        if (active) setSpocs([]);
      }
    };
    load();
    return () => { active = false; };
  }, [clientId]);

  const selected = spocs.find(s => s.id === value);

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(!open)} className="w-full p-2 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border border-glass-border dark:border-glass-border-dark-mode dark:border-glass-border-dark-mode rounded-md text-left">
        {selected ? `${selected.name}${selected.email ? ' • ' + selected.email : ''}` : (clientId ? 'Select SPOC' : 'Select a client first')}
      </button>
      {open && clientId && (
        <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl shadow-xl">
          <div className="max-h-64 overflow-y-auto py-1">
            {spocs.length === 0 && (
              <div className="px-4 py-3 text-sm text-gray-500">No SPOCs found</div>
            )}
            {spocs.map(spoc => (
              <button key={spoc.id} type="button" onClick={() => { onChange(spoc); setOpen(false); }} className={`w-full px-4 py-2 text-left hover:bg-blue-50 dark:hover:bg-gray-700 ${value === spoc.id ? 'bg-blue-100 dark:bg-gray-700/40 font-medium' : ''}`}>
                <div className="text-sm">{spoc.name}</div>
                {(spoc.email || spoc.phone) && <div className="text-xs text-gray-500">{spoc.email || spoc.phone}</div>}
              </button>
            ))}
          </div>
        </div>
      )}
      {open && <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />}
    </div>
  );
};

// Enhanced requirement schema - only title, client, status, and mandatory skills are required
const requirementSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  positions: z.union([z.string(), z.number()]).transform((val) => {
    if (val === '' || val === null || val === undefined) return 1;
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return isNaN(num) ? 1 : Math.floor(num); // Positions must be whole numbers
  }).pipe(z.number().min(1, 'No. of Positions must be at least 1').max(500, 'Too many positions')).optional(),
  client_id: z.string().min(1, 'Client is required'),
  client_name: z.string().optional(),
  spoc_id: z.string().optional(),
  spoc_name: z.string().optional(),
  source: z.enum(['email', 'client_portal']).default('email'),
  description: z.string().optional(),
  skills: z.object({
    mandatory: z.array(z.string()).min(1, 'At least one mandatory skill is required'),
    optional: z.array(z.string()).optional()
  }),
  seniority: z.string().optional(),
  location: z.object({
    city: z.string().optional(),
    state: z.string().optional(),
    country: z.string().optional(),
    remote_work: z.boolean().default(false),
    relocation_assistance: z.boolean().default(false)
  }).optional(),
  compensation: z.object({
    salary_range: z.object({
      min: z.union([z.string(), z.number()]).transform((val) => {
        if (val === '' || val === null || val === undefined) return undefined;
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? undefined : num;
      }).pipe(z.number().min(0, 'Minimum salary must be positive').max(10000000, 'Salary too high')).optional(),
      max: z.union([z.string(), z.number()]).transform((val) => {
        if (val === '' || val === null || val === undefined) return undefined;
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? undefined : num;
      }).pipe(z.number().min(0, 'Maximum salary must be positive').max(10000000, 'Salary too high')).optional(),
      currency: z.string().optional()
    }).refine((data) => {
      if (data.min !== undefined && data.max !== undefined) {
        return data.max >= data.min;
      }
      return true;
    }, {
      message: "Maximum salary must be greater than or equal to minimum salary",
      path: ["max"]
    }).optional(),
    benefits: z.array(z.string()).optional(),
    equity: z.boolean().default(false),
    bonus: z.boolean().default(false)
  }).optional(),
  status: z.string().default('active'),
  employment_type: z.string().optional(),
  experience: z.object({
    min_years: z.union([z.string(), z.number()]).transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return isNaN(num) ? undefined : num;
    }).pipe(z.number().min(0, 'Years must be positive').max(50, 'Years cannot exceed 50')).optional(),
    max_years: z.union([z.string(), z.number()]).transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return isNaN(num) ? undefined : num;
    }).pipe(z.number().min(0, 'Years must be positive').max(50, 'Years cannot exceed 50')).optional()
  }).refine((data) => {
    if (data.min_years !== undefined && data.max_years !== undefined) {
      return data.max_years >= data.min_years;
    }
    return true;
  }, {
    message: "Maximum years must be greater than or equal to minimum years",
    path: ["max_years"]
  }).optional(),
  education: z.string().optional(),
  category: z.string().optional(),
  notice_period: z.union([z.string(), z.number()]).transform((val) => {
    if (val === '' || val === null || val === undefined) return 30;
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return isNaN(num) ? 30 : Math.floor(num);
  }).pipe(z.number().min(0, 'Notice period must be positive').max(365, 'Notice period cannot exceed 365 days')).default(30).optional(),
  additional_details: z.object({
    travel_requirements: z.string().optional(),
    visa_sponsorship: z.boolean().default(false),
    security_clearance: z.boolean().default(false),
    certifications: z.array(z.string()).optional()
  }).optional()
});

type RequirementFormData = z.infer<typeof requirementSchema>;

interface RequirementFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  editData?: any; // Data for editing existing requirement
  mode?: 'create' | 'edit'; // Form mode
}

export const RequirementForm: React.FC<RequirementFormProps> = ({ 
  onSuccess, 
  onCancel,
  editData,
  mode = 'create'
}) => {
  const { confirm } = useConfirmation();
  const [isUploading, setIsUploading] = useState(false);

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [showProcessingProgress, setShowProcessingProgress] = useState(false);
  const [isManualParsing, setIsManualParsing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadStage, setCurrentUploadStage] = useState<string>('');

  const createRequirement = useCreateRequirement();
  const updateRequirement = useUpdateRequirement();

  // Transform edit data to form default values
  const getDefaultValues = () => {
    if (mode === 'edit' && editData) {
      // Handle both parsed data structure and flat database structure
      const parsedInsights = editData.parsed_insights || {};
      
      return {
        title: editData.title || '',
        positions: (editData.positions ? Number(editData.positions) : 1),
        client_id: editData.client_id || '',
        client_name: editData.client_name || '',
        spoc_id: editData.spoc_id || '',
        spoc_name: editData.spoc_name || '',
        source: (editData.source as 'email' | 'client_portal') || 'email',
        description: editData.description || '',
        skills: {
          mandatory: parsedInsights.skills?.mandatory || parsedInsights.skills?.mandatory_skills || editData.skills?.mandatory_skills || editData.skills?.mandatory || [],
          optional: parsedInsights.skills?.optional || parsedInsights.skills?.optional_skills || editData.skills?.optional_skills || editData.skills?.optional || []
        },
        seniority: parsedInsights.seniority || editData.seniority || '',
        location: {
          city: parsedInsights.location?.city || editData.location?.city || '',
          state: parsedInsights.location?.state || editData.location?.state || '',
          country: parsedInsights.location?.country || editData.location?.country || '',
          remote_work: parsedInsights.location?.remote_work || editData.location?.remote_work || false,
          relocation_assistance: parsedInsights.location?.relocation_assistance || editData.location?.relocation_assistance || false
        },
        compensation: {
          salary_range: {
            min: parsedInsights.compensation?.salary_range?.min || editData.compensation?.salary_range?.min || undefined,
            max: parsedInsights.compensation?.salary_range?.max || editData.compensation?.salary_range?.max || undefined,
            currency: parsedInsights.compensation?.salary_range?.currency || editData.compensation?.salary_range?.currency || ''
          },
          benefits: parsedInsights.compensation?.benefits || editData.compensation?.benefits || [],
          equity: parsedInsights.compensation?.equity || editData.compensation?.equity || false,
          bonus: parsedInsights.compensation?.bonus || editData.compensation?.bonus || false
        },
        status: editData.status || 'active',
        employment_type: parsedInsights.employment_type || editData.employment_type || 'full_time',
        experience: {
          min_years: (() => {
            const value = parsedInsights.experience?.min_years || editData.experience?.min_years;
            return value ? Number(value) : undefined;
          })(),
          max_years: (() => {
            const value = parsedInsights.experience?.max_years || editData.experience?.max_years;
            return value ? Number(value) : undefined;
          })()
        },
        education: parsedInsights.education || editData.education || '',
        category: parsedInsights.category || editData.category || '',
        notice_period: (() => {
          const value = parsedInsights.notice_period || editData.notice_period;
          return value ? Number(value) : 30;
        })(),
        additional_details: {
          visa_sponsorship: parsedInsights.additional_details?.visa_sponsorship || editData.additional_details?.visa_sponsorship || false,
          security_clearance: parsedInsights.additional_details?.security_clearance || editData.additional_details?.security_clearance || false
        }
      };
    }
    
    // Default values for create mode
    return {
      title: '',
      positions: 1,
      client_id: '',
      client_name: '',
      source: 'email' as const,
      jd_content: '',
      skills: { mandatory: [], optional: [] },
      location: { remote_work: false, relocation_assistance: false },
      compensation: { equity: false, bonus: false },
      experience: { min_years: undefined, max_years: undefined },
      education: '',
      category: '',
      notice_period: 30,
      status: 'active',
      additional_details: { visa_sponsorship: false, security_clearance: false }
    };
  };
  
  // JD Processing hook
  const jdProcessing = useJDProcessing({
    pollingInterval: 10000, // 10 seconds
    autoStart: true,
    onComplete: (status) => {
      console.log('🎉 JD Processing completed:', status);
      setCurrentUploadStage('Processing completed!');
      setUploadProgress(100);
      
      if (status.parsed_data) {
        // Auto-fill form with parsed data
        autoFillFormWithParsedData(status.parsed_data);
        toast.success('JD parsed successfully! Form has been auto-filled.');
      } else {
        toast.success('JD uploaded successfully!');
      }
      
      // Hide progress after a short delay to show completion
      setTimeout(() => {
        setShowProcessingProgress(false);
        setIsUploading(false);
        setUploadProgress(0);
        setCurrentUploadStage('');
      }, 2000);
    },
    onError: (error) => {
      console.error('❌ JD Processing failed:', error);
      setCurrentUploadStage('Processing failed');
      setUploadProgress(0);
      toast.error(`Failed to parse JD: ${error}`);
      
      // Hide progress after showing error
      setTimeout(() => {
        setShowProcessingProgress(false);
        setIsUploading(false);
        setCurrentUploadStage('');
      }, 3000);
    }
  });

  // Monitor JD processing status for progress updates
  React.useEffect(() => {
    if (jdProcessing.currentStatus) {
      const status = jdProcessing.currentStatus;
      console.log('📊 JD Processing progress:', status);
      setCurrentUploadStage(stageLabels[status.stage] || 'Processing...');
      setUploadProgress(status.progress || 0);
    }
  }, [jdProcessing.currentStatus]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting, isValid }
  } = useForm<RequirementFormData>({
    resolver: zodResolver(requirementSchema),
    defaultValues: getDefaultValues(),
    mode: 'onChange' // Enable real-time validation
  });

  // Reset form when editData changes
  React.useEffect(() => {
    if (mode === 'edit' && editData) {
      const defaultValues = getDefaultValues();
      
      // Reset the entire form
      reset(defaultValues);
      
      // Explicitly set skills to ensure they're properly loaded
      if (defaultValues.skills?.mandatory) {
        setValue('skills.mandatory', defaultValues.skills.mandatory);
      }
      if (defaultValues.skills?.optional) {
        setValue('skills.optional', defaultValues.skills.optional);
      }
    }
  }, [editData, mode, reset, setValue]);

  // Debug form values - only log when form changes significantly
  const formValues = watch();
  const hasRequiredFields = !!(formValues.title && formValues.client_id && formValues.skills?.mandatory?.length > 0);
  
  // Only log when form state changes significantly
  React.useEffect(() => {
    console.log('🔍 Form state update:', {
      hasTitle: !!formValues.title,
      hasClientId: !!formValues.client_id,
      hasMandatorySkills: formValues.skills?.mandatory?.length > 0,
      hasRequiredFields,
      isValid,
      errors: Object.keys(errors).length > 0 ? errors : 'No errors'
    });
  }, [formValues.title, formValues.client_id, formValues.skills?.mandatory, isValid, errors]);

  // Function to handle manual JD parsing
  const handleManualParse = async () => {
    const description = watch('description');
    if (!description || description.trim() === '') {
      confirm({
        title: 'Validation Error',
        description: 'Please enter job description content to parse',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => {}
      });
      return;
    }

    setIsManualParsing(true);
    setShowProcessingProgress(true);
    setUploadProgress(0);
    setCurrentUploadStage('Preparing text content...');

    // Simulate progress for manual parsing
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          setCurrentUploadStage('Processing text content...');
          return 90;
        }
        return prev + 15;
      });
    }, 200);

    try {
      console.log('🚀 Frontend: Starting async JD processing for text content');
      
      // Start async JD processing with text
      jdProcessing.startProcessing({ text: description });
    } catch (error) {
      console.error('❌ Manual parsing failed:', error);
      clearInterval(progressInterval);
      confirm({
        title: 'Parse Failed',
        description: 'Failed to parse job description. Please try again.',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => {}
      });
      setIsManualParsing(false);
      setShowProcessingProgress(false);
      setUploadProgress(0);
      setCurrentUploadStage('');
    }
  };

  // Function to auto-fill form with parsed JD data
  const autoFillFormWithParsedData = (parsedData: any) => {
    console.log('🔍 Auto-filling form with parsed data:', parsedData);
    console.log('🔍 Skills data structure:', parsedData.skills);
    console.log('🔍 Mandatory skills:', parsedData.skills?.mandatory);
    console.log('🔍 Optional skills:', parsedData.skills?.optional);
    
    // Auto-fill form with parsed data
    if (parsedData.title) setValue('title', parsedData.title);
    if (parsedData.description) setValue('description', parsedData.description);
    
    // Reset parsing states
    setIsManualParsing(false);
    setShowProcessingProgress(false);
    
    // Skills mapping
    if (parsedData.skills?.mandatory) {
      console.log('🔧 Setting mandatory skills:', parsedData.skills.mandatory);
      setValue('skills.mandatory', parsedData.skills.mandatory);
    }
    if (parsedData.skills?.optional) {
      console.log('⭐ Setting optional skills:', parsedData.skills.optional);
      setValue('skills.optional', parsedData.skills.optional);
    }
    
    // Experience mapping - convert to numbers
    if (parsedData.experience?.min_years) {
      const minYears = Number(parsedData.experience.min_years);
      console.log('🔧 Converting min_years:', parsedData.experience.min_years, 'to number:', minYears);
      if (!isNaN(minYears) && minYears >= 0) {
        setValue('experience.min_years', minYears);
        console.log('✅ Set min_years to:', minYears);
      }
    }
    if (parsedData.experience?.max_years) {
      const maxYears = Number(parsedData.experience.max_years);
      console.log('🔧 Converting max_years:', parsedData.experience.max_years, 'to number:', maxYears);
      if (!isNaN(maxYears) && maxYears >= 0) {
        setValue('experience.max_years', maxYears);
        console.log('✅ Set max_years to:', maxYears);
      }
    }
    
    // Location mapping
    if (parsedData.location?.city) setValue('location.city', parsedData.location.city);
    if (parsedData.location?.state) setValue('location.state', parsedData.location.state);
    if (parsedData.location?.country) setValue('location.country', parsedData.location.country);
    if (parsedData.location?.remote_work !== undefined) {
      setValue('location.remote_work', parsedData.location.remote_work);
    }
    if (parsedData.location?.relocation_assistance !== undefined) {
      setValue('location.relocation_assistance', parsedData.location.relocation_assistance);
    }
    
    // Notice period mapping
    if (parsedData.notice_period !== undefined && parsedData.notice_period !== null) {
      const noticePeriod = Number(parsedData.notice_period);
      if (!isNaN(noticePeriod) && noticePeriod >= 0 && noticePeriod <= 365) {
        setValue('notice_period', noticePeriod);
      } else {
        setValue('notice_period', 30);
      }
    } else {
      setValue('notice_period', 30);
    }
    
    // Compensation mapping
    if (parsedData.compensation?.salary_range?.min) setValue('compensation.salary_range.min', parsedData.compensation.salary_range.min);
    if (parsedData.compensation?.salary_range?.max) setValue('compensation.salary_range.max', parsedData.compensation.salary_range.max);
    if (parsedData.compensation?.salary_range?.currency) setValue('compensation.salary_range.currency', parsedData.compensation.salary_range.currency);
    if (parsedData.compensation?.benefits) setValue('compensation.benefits', parsedData.compensation.benefits);
    if (parsedData.compensation?.equity !== undefined) setValue('compensation.equity', parsedData.compensation.equity);
    if (parsedData.compensation?.bonus !== undefined) setValue('compensation.bonus', parsedData.compensation.bonus);
    
    // Employment type
    if (parsedData.employment_type) setValue('employment_type', parsedData.employment_type);
    
    // Education
    if (parsedData.education) setValue('education', parsedData.education);
    
    // Category
    if (parsedData.category) setValue('category', parsedData.category);
    
    // Additional details
    if (parsedData.additionalDetails?.certifications) setValue('additional_details.certifications', parsedData.additionalDetails.certifications);
    
    console.log('🎉 Form auto-filled successfully!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    console.log('🔍 Frontend: handleFileUpload called with file:', {
      fileName: file?.name,
      fileSize: file?.size,
      fileType: file?.type,
      lastModified: file?.lastModified
    });
    
    if (file) {
      setUploadedFile(file);
      setIsUploading(true);
      setUploadProgress(0);
      setCurrentUploadStage('Preparing file...');
      
      // Show progress immediately when file is uploaded
      setShowProcessingProgress(true);
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            setCurrentUploadStage('Uploading to server...');
            return 90;
          }
          return prev + 10;
        });
      }, 100);
      
      console.log('🚀 Frontend: Starting async JD processing for file:', file.name);
      
      // Start async JD processing
      jdProcessing.startProcessing({ file });
    }
  };



  // Function to clean form data before submission
  const cleanFormData = (data: any): any => {
    const cleaned = { ...data };
    
    // Remove empty strings and convert to undefined
    const cleanValue = (value: any): any => {
      if (value === '' || value === null) return undefined;
      
      // Handle arrays specially - keep them as arrays
      if (Array.isArray(value)) {
        const cleanedArray = value
          .map(item => cleanValue(item))
          .filter(item => item !== undefined && item !== '');
        return cleanedArray.length > 0 ? cleanedArray : undefined;
      }
      
      if (typeof value === 'object' && value !== null) {
        const cleanedObj: any = {};
        for (const [key, val] of Object.entries(value)) {
          const cleanedVal = cleanValue(val);
          if (cleanedVal !== undefined) {
            cleanedObj[key] = cleanedVal;
          }
        }
        return Object.keys(cleanedObj).length > 0 ? cleanedObj : undefined;
      }
      return value;
    };
    
    // Clean all fields
    for (const [key, value] of Object.entries(cleaned)) {
      cleaned[key] = cleanValue(value);
    }
    
    // Remove undefined values
    Object.keys(cleaned).forEach(key => {
      if (cleaned[key] === undefined) {
        delete cleaned[key];
      }
    });
    
    return cleaned;
  };

  const onSubmit = (data: RequirementFormData) => {
    console.log('📝 Form submission data:', data);
    console.log('📝 Experience data:', data.experience);
    console.log('📝 Min years type:', typeof data.experience?.min_years, 'value:', data.experience?.min_years);
    console.log('📝 Max years type:', typeof data.experience?.max_years, 'value:', data.experience?.max_years);
    console.log('📝 Form errors:', errors);
    console.log('📝 Form isSubmitting:', isSubmitting);
    console.log('📝 Form isValid:', isValid);
    
    // Clean the form data before submission
    const cleanedData = cleanFormData(data);
    console.log('🧹 Cleaned form data:', cleanedData);
    
    if (mode === 'edit' && editData?.id) {
      // Update existing requirement
      updateRequirement.mutate(
        { id: editData.id, data: cleanedData },
        {
          onSuccess: () => {
            onSuccess?.();
          }
        }
      );
    } else {
      // Create new requirement
      createRequirement.mutate(cleanedData, {
        onSuccess: () => {
          // Reset form to default values after successful creation
          const defaultValues = getDefaultValues();
          reset(defaultValues);
          onSuccess?.();
        }
      });
    }
  };

  return (
    <div className="p-8">
        {/* Form Action Buttons - Floating Glassmorphic */}
        <div className="sticky top-4 z-20 mb-8">
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              onClick={onCancel}
              variant="outline"
              size="sm"
              className="glass-button backdrop-blur-xl border-glass-border dark:border-glass-border-dark-mode/50 hover:border-primary-500/50 transition-all duration-300 hover:scale-105"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="requirement-form"
              disabled={isSubmitting}
              variant="default"
              size="sm"
              className="flex items-center gap-2 glass-button backdrop-blur-xl bg-gradient-to-r from-primary-500/20 to-cyan-500/20 border border-primary-500/30 hover:shadow-glow-primary transition-all duration-300 hover:scale-105"
              onClick={() => {
                console.log('🔘 Submit button clicked');
                console.log('🔘 Form errors:', errors);
                console.log('🔘 Form isValid:', isValid);
                console.log('🔘 Form isSubmitting:', isSubmitting);
              }}
            >
              {isSubmitting || createRequirement.isPending || updateRequirement.isPending ? (
                <>
                  <LoadingSpinner size="sm" variant="ai" />
                  {mode === 'edit' ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {mode === 'edit' ? 'Update Requirement' : 'Create Requirement'}
                </>
              )}
            </Button>
          </div>
        </div>

        <form 
          id="requirement-form" 
          onSubmit={(e) => {
            console.log('📋 Form submit event triggered');
            console.log('📋 Form errors:', errors);
            console.log('📋 Form isValid:', isValid);
            handleSubmit(onSubmit)(e);
          }}
          className="space-y-6"
          key={mode === 'edit' && editData ? `edit-${editData.id}` : 'create'}
        >
          {/* Job Description and Upload - First Row */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-cyan-400 rounded-full"></div>
              <h3 className="text-lg font-semibold text-text-enterprise-primary">Job Description Input</h3>
            </div>
            
            {/* Job Description Input - Upload OR Text */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* File Upload */}
              <div className="lg:col-span-1">
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">
                  Upload JD (PDF, Word .docx, or EML)
                </label>
                <div className="border-2 border-dashed border-glass-border dark:border-glass-border-dark-mode dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode rounded-lg p-4 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 transition-colors h-full min-h-[200px]">
                  <input
                    type="file"
                    accept=".pdf,.docx,.eml"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="jd-upload"
                    disabled={isUploading}
                  />
                  <label
                    htmlFor="jd-upload"
                    className={`cursor-pointer flex flex-col items-center justify-center space-y-2 h-full ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {uploadedFile ? (
                      // Show uploaded file info within the upload box
                      <div className="flex flex-col items-center space-y-3 w-full">
                        <div className="flex items-center gap-2 p-3 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 rounded-lg border border-glass-border dark:border-glass-border-dark-mode dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode w-full">
                          <FileText className="w-5 h-5 text-primary-400" />
                          <span className="text-sm text-text-enterprise-primary font-medium truncate flex-1">
                            {uploadedFile.name}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              setUploadedFile(null);
                            }}
                            className="text-text-enterprise-error hover:text-text-enterprise-error p-1"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-text-enterprise-secondary">
                            Click to upload a different file
                          </p>
                        </div>
                      </div>
                    ) : (
                      // Show default upload state
                      <>
                        <Upload className="w-8 h-8 text-primary-400" />
                        <div className="text-center">
                          <p className="text-sm font-medium text-text-enterprise-primary">
                            {isUploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                          </p>
                          <p className="text-xs text-text-enterprise-secondary">
                            PDF, Word (.docx), or EML files
                          </p>
                        </div>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Job Description Text Area */}
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-text-enterprise-secondary">
                    Job Description (Copy & Paste)
                  </label>
                  <Button
                    type="button"
                    onClick={handleManualParse}
                    disabled={isManualParsing || isUploading}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    {isManualParsing ? (
                      <>
                        <LoadingSpinner size="sm" variant="ai" />
                        Parsing...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        Parse Text
                      </>
                    )}
                  </Button>
                </div>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      rows={8}
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary placeholder-text-enterprise-tertiary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50 h-full min-h-[200px]"
                      placeholder="Copy and paste the job description here, then click Parse Text to extract structured information..."
                    />
                  )}
                />
                {errors.description && <p className="text-text-enterprise-error text-sm mt-1">{errors.description.message as any}</p>}
                
              </div>
            </div>
          </div>

          {/* Basic Information Section - 4 Column Layout */}
          <div className="space-y-6 mt-24">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-gradient-to-b from-cyan-400 to-purple-400 rounded-full"></div>
              <h3 className="text-lg font-semibold text-text-enterprise-primary">Basic Information</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Title *</label>
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <input {...field} type="text" className="w-full p-3 bg-glass-surface/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary placeholder-text-enterprise-tertiary transition-all duration-300 hover:bg-glass-surface/40 focus:bg-glass-surface/50" placeholder="e.g., Senior Software Engineer" />
                  )}
                />
                {errors.title && <p className="text-text-enterprise-error text-sm mt-1">{errors.title.message as any}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Client *</label>
                <Controller
                  name="client_id"
                  control={control}
                  render={({ field }) => (
                    <ClientSelector
                      value={field.value}
                      onChange={(clientId, client) => {
                        field.onChange(clientId);
                        setValue('client_name', client?.client_name || '');
                        setValue('spoc_id', '');
                      }}
                    />
                  )}
                />
                {errors.client_id && <p className="text-text-enterprise-error text-sm mt-1">{errors.client_id.message as any}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Source *</label>
                <Controller
                  name="source"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                    >
                      <option value="email">Email</option>
                      <option value="client_portal">Client Portal</option>
                    </select>
                  )}
                />
                {errors.source && <p className="text-text-enterprise-error text-sm mt-1">{errors.source.message as any}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">SPOC</label>
                <Controller
                  name="spoc_id"
                  control={control}
                  render={({ field }) => (
                    <SPOCSelector
                      clientId={watch('client_id')}
                      value={field.value}
                      onChange={(spoc) => field.onChange(spoc?.id || '')}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* Skills Section - Draggable Skills Input */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-purple-400 to-pink-400 rounded-full"></div>
              <h3 className="text-lg font-semibold text-text-enterprise-primary">Skills Requirements</h3>
            </div>
            
            <Controller
              name="skills"
              control={control}
              render={({ field }) => (
                <DraggableSkillsInput
                  mandatorySkills={field.value?.mandatory || []}
                  optionalSkills={field.value?.optional || []}
                  onMandatoryChange={(skills) => field.onChange({ ...field.value, mandatory: skills })}
                  onOptionalChange={(skills) => field.onChange({ ...field.value, optional: skills })}
                  error={errors.skills?.mandatory?.message as string}
                />
              )}
            />
          </div>



          {/* Job Details - 6 Column Layout */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-green-400 to-blue-400 rounded-full"></div>
              <h3 className="text-lg font-semibold text-text-enterprise-primary">Job Details</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Positions</label>
                <Controller
                  name="positions"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="number"
                      min="1"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="1"
                    />
                  )}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Employment Type</label>
                <Controller
                  name="employment_type"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                    >
                      <option value="">Select type</option>
                      <option value="full-time">Full Time</option>
                      <option value="part-time">Part Time</option>
                      <option value="contract">Contract</option>
                      <option value="temporary">Temporary</option>
                      <option value="internship">Internship</option>
                    </select>
                  )}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Seniority Level</label>
                <Controller
                  name="seniority"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                    >
                      <option value="">Select level</option>
                      <option value="entry">Entry Level</option>
                      <option value="junior">Junior</option>
                      <option value="mid">Mid Level</option>
                      <option value="senior">Senior</option>
                      <option value="lead">Lead</option>
                      <option value="principal">Principal</option>
                      <option value="director">Director</option>
                    </select>
                  )}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Experience Min (Years)</label>
                <Controller
                  name="experience.min_years"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="number"
                      min="0"
                      max="50"
                      step="0.5"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="0"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          field.onChange(undefined);
                        } else {
                          const numValue = parseFloat(value);
                          field.onChange(isNaN(numValue) ? value : numValue);
                        }
                      }}
                    />
                  )}
                />
                {errors.experience?.min_years && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.experience.min_years.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Experience Max (Years)</label>
                <Controller
                  name="experience.max_years"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="number"
                      min="0"
                      max="50"
                      step="0.5"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="5"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          field.onChange(undefined);
                        } else {
                          const numValue = parseFloat(value);
                          field.onChange(isNaN(numValue) ? value : numValue);
                        }
                      }}
                    />
                  )}
                />
                {errors.experience?.max_years && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.experience.max_years.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Job Category</label>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                    >
                      <option value="">Select category</option>
                      <option value="engineering">Engineering</option>
                      <option value="product">Product</option>
                      <option value="design">Design</option>
                      <option value="marketing">Marketing</option>
                      <option value="sales">Sales</option>
                      <option value="operations">Operations</option>
                      <option value="finance">Finance</option>
                      <option value="hr">Human Resources</option>
                      <option value="other">Other</option>
                    </select>
                  )}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Status</label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                    >
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="closed">Closed</option>
                      <option value="draft">Draft</option>
                    </select>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Compensation and Location - 6 Column Layout */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-green-400 to-blue-400 rounded-full"></div>
              <h3 className="text-lg font-semibold text-text-enterprise-primary">Compensation & Location</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Salary Min (LPA)</label>
                <Controller
                  name="compensation.salary_range.min"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="number"
                      min="0"
                      max="10000000"
                      step="1000"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="Min"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          field.onChange(undefined);
                        } else {
                          const numValue = parseFloat(value);
                          field.onChange(isNaN(numValue) ? value : numValue);
                        }
                      }}
                    />
                  )}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Salary Max (LPA)</label>
                <Controller
                  name="compensation.salary_range.max"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="number"
                      min="0"
                      max="10000000"
                      step="1000"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="Max"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          field.onChange(undefined);
                        } else {
                          const numValue = parseFloat(value);
                          field.onChange(isNaN(numValue) ? value : numValue);
                        }
                      }}
                    />
                  )}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Currency</label>
                <Controller
                  name="compensation.salary_range.currency"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                    >
                      <option value="">Select currency</option>
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="GBP">GBP (£)</option>
                    </select>
                  )}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">City</label>
                <Controller
                  name="location.city"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="e.g., Bangalore"
                    />
                  )}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">State</label>
                <Controller
                  name="location.state"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="e.g., Karnataka"
                    />
                  )}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Country</label>
                <Controller
                  name="location.country"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="e.g., India"
                    />
                  )}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center">
                <Controller
                  name="location.remote_work"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-glass-border dark:border-glass-border-dark-mode rounded"
                    />
                  )}
                />
                <label className="ml-2 block text-sm text-text-enterprise-primary">
                  Remote Work
                </label>
              </div>
              <div className="flex items-center">
                <Controller
                  name="location.relocation_assistance"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-glass-border dark:border-glass-border-dark-mode rounded"
                    />
                  )}
                />
                <label className="ml-2 block text-sm text-text-enterprise-primary">
                  Relocation Assistance
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Benefits</label>
                <Controller
                  name="compensation.benefits"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="e.g., Health, Dental, 401k"
                    />
                  )}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <Controller
                  name="compensation.equity"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-glass-border dark:border-glass-border-dark-mode rounded"
                    />
                  )}
                />
                <label className="ml-2 block text-sm text-text-enterprise-primary">
                  Equity/Stock Options
                </label>
              </div>
              <div className="flex items-center">
                <Controller
                  name="compensation.bonus"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-glass-border dark:border-glass-border-dark-mode rounded"
                    />
                  )}
                />
                <label className="ml-2 block text-sm text-text-enterprise-primary">
                  Performance Bonus
                </label>
              </div>
            </div>
          </div>

          {/* Education & Certifications - 2 Column Layout */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-blue-400 to-indigo-400 rounded-full"></div>
              <h3 className="text-lg font-semibold text-text-enterprise-primary">Education & Certifications</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Education</label>
                <Controller
                  name="education"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="e.g., B.Tech, MCA, Any Graduate"
                    />
                  )}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Notice Period (Days)</label>
                <Controller
                  name="notice_period"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="number"
                      min="0"
                      max="365"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="30"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          field.onChange(30);
                        } else {
                          const numValue = parseInt(value, 10);
                          field.onChange(isNaN(numValue) ? 30 : numValue);
                        }
                      }}
                    />
                  )}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Certifications</label>
                <Controller
                  name="additional_details.certifications"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="e.g., AWS Certified, PMP"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* Additional Details - 4 Column Layout */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-orange-400 to-red-400 rounded-full"></div>
              <h3 className="text-lg font-semibold text-text-enterprise-primary">Additional Details</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center justify-center">
                <div className="flex items-center">
                  <Controller
                    name="additional_details.security_clearance"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-glass-border dark:border-glass-border-dark-mode rounded"
                      />
                    )}
                  />
                  <label className="ml-2 block text-sm text-text-enterprise-primary">
                    Security Clearance
                  </label>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <div className="flex items-center">
                  <Controller
                    name="additional_details.visa_sponsorship"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-glass-border dark:border-glass-border-dark-mode rounded"
                      />
                    )}
                  />
                  <label className="ml-2 block text-sm text-text-enterprise-primary">
                    Visa Sponsorship
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Travel Required</label>
                <Controller
                  name="additional_details.travel_requirements"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="e.g., 25% travel"
                    />
                  )}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">Benefits</label>
                <Controller
                  name="compensation.benefits"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="w-full p-3 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 backdrop-blur-xl border border-glass-border dark:border-glass-border-dark-mode/50 dark:border-glass-border dark:border-glass-border-dark-mode-dark-mode/50 rounded-lg focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-text-enterprise-primary transition-all duration-300 hover:bg-glass-surface/40 dark:hover:bg-glass-surface-dark-mode/40 focus:bg-glass-surface/50 dark:focus:bg-glass-surface-dark-mode/50"
                      placeholder="e.g., Health, Dental, 401k"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* Success Message */}
          {createRequirement.isSuccess && (
            <div className="flex items-center gap-2 p-4 bg-green-100 text-green-800 rounded-md">
              <CheckCircle className="w-5 h-5" />
              <span>Requirement created successfully!</span>
            </div>
          )}

          {/* Error Message */}
          {createRequirement.error && (
            <div className="p-4 bg-red-100 text-red-800 rounded-md">
              Error: {createRequirement.error.message}
            </div>
          )}
        </form>
      
      {/* JD Processing Progress Modal */}
      {showProcessingProgress && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-[99999] pt-8">
          <div className="bg-white dark:bg-gray-900 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-8 max-w-4xl w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <LoadingSpinner size="lg" variant="ai" className="text-primary-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Processing Job Description
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {uploadedFile ? `Processing ${uploadedFile.name}...` : 'Please wait while we extract and analyze the job requirements...'}
              </p>
              
              {/* Show custom progress if no JD processing status yet */}
              {!jdProcessing.currentStatus ? (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-left">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Uploading File
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {uploadedFile?.name} • {uploadedFile ? Math.round(uploadedFile.size / 1024) : 0} KB
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary-500">
                        {uploadProgress}%
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {currentUploadStage || 'In Progress'}
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-6">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-500 h-3 rounded-full transition-all duration-500 ease-out shadow-glow-primary"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>

                  {/* Stage Indicators */}
                  <div className="grid grid-cols-5 gap-2 mb-6">
                    {['Preparing', 'Uploading', 'Extracting', 'Parsing', 'Validating'].map((stage, index) => {
                      const isActive = currentUploadStage.includes(stage.toLowerCase()) || 
                        (stage === 'Preparing' && uploadProgress < 20) ||
                        (stage === 'Uploading' && uploadProgress >= 20 && uploadProgress < 40) ||
                        (stage === 'Extracting' && uploadProgress >= 40 && uploadProgress < 60) ||
                        (stage === 'Parsing' && uploadProgress >= 60 && uploadProgress < 80) ||
                        (stage === 'Validating' && uploadProgress >= 80);
                      const isCompleted = uploadProgress > (index + 1) * 20;
                      
                      return (
                        <div
                          key={stage}
                          className={`flex flex-col items-center p-3 rounded-lg transition-all duration-300 ${
                            isActive 
                              ? 'bg-primary-100 dark:bg-primary-900/40 border-2 border-primary-500/50 shadow-glow-primary' 
                              : isCompleted 
                                ? 'bg-green-100 dark:bg-green-900/40 border border-green-500/40'
                                : 'bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600'
                          }`}
                        >
                          <div className={`text-2xl mb-1 transition-all duration-300 ${
                            isActive ? 'text-primary-500' : isCompleted ? 'text-green-500' : 'text-gray-400 dark:text-gray-500'
                          }`}>
                            {['📁', '📤', '📝', '🤖', '✅'][index]}
                          </div>
                          <div className={`text-xs font-medium text-center transition-all duration-300 ${
                            isActive ? 'text-gray-900 dark:text-white' : isCompleted ? 'text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {stage}
                          </div>
                          {isActive && (
                            <div className="mt-1">
                              <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse shadow-glow-primary" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Status Message */}
                  <div className="text-center">
                    <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      {currentUploadStage || 'Preparing your file for processing...'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      This may take a few moments depending on the document size.
                    </div>
                  </div>
                </div>
              ) : (
                <JDProcessingProgress 
                  status={jdProcessing.currentStatus} 
                  className="border-2 border-primary-200 dark:border-primary-700 rounded-lg"
                />
              )}
              
              <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
                This may take a few moments depending on the document size.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequirementForm;
